/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import toast from "react-hot-toast"
import {
  selectAdasProgramsMetaData, selectSensingSystemRequirementsGenerateCombinedReportDialogOpen,
  selectSensingSystemRequirementsTableDataSetStatusMap,
  selectSensingSystemRequirementsTableRows,
  selectSensingSystemRequirementsTableSelectedRows,
  selectSensingSystemRequirementsUpdateReportDialogOpen,
  selectSensorComponentRequirementsGenerateCombinedReportDialogOpen,
  selectSensorComponentRequirementsTableDataSetStatusMap,
  selectSensorComponentRequirementsTableRows,
  selectSensorComponentRequirementsTableSelectedRows,
  selectSensorComponentRequirementsUpdateReportDialogOpen,
  selectWorldModelSolutionRequirementsGenerateCombinedReportDialogOpen,
  selectWorldModelSolutionRequirementsTableDataSetStatusMap,
  selectWorldModelSolutionRequirementsTableRows,
  selectWorldModelSolutionRequirementsTableSelectedRows,
  selectWorldModelSolutionRequirementsUpdateReportDialogOpen,
  setSensingSystemRequirementsGenerateCombinedReportDialogOpen,
  setSensingSystemRequirementsUpdateReportDialogOpen, setSensorComponentRequirementsGenerateCombinedReportDialogOpen,
  setSensorComponentRequirementsUpdateReportDialogOpen,
  setWorldModelSolutionRequirementsGenerateCombinedReportDialogOpen,
  setWorldModelSolutionRequirementsUpdateReportDialogOpen
} from "../../../../../../redux/ducks/AdasPrograms/slice"
import {useAppDispatch, useAppSelector} from "../../../../../../redux/hooks"
import ArgosDialog from "../../../../../components/Dialogs/ArgosDialog"
import {
  ADAS_PROGRAMS_REPORT_PAGE_NAME_MAP,
  AdasProgramsReportPageIdx,
  AdasProgramsReportPages,
  EUpdateReportsStatus,
  GreenLineReportsTableData,
  RedLineReportsTableData
} from "../constants"
import {usePostRequest} from "../../../../../../api/helper"
import {CAdasProgramsRequests} from "../../types"
import {EArgosReportsState} from "../../../../../../constants/colors"
import Loader from "../../../../../components/Loader/Loader"
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox"
import {BulletList} from "../../../../../components/Lists/BulletList"
import ArgosButton, {ArgosLoadingButton} from "../../../../../components/Buttons/ArgosButton"
import {useEffect, useMemo, useState} from "react"
import ADAS_PROGRAMS_CONSTANTS from "../../../constants"
import {StringParam, useQueryParam} from "use-query-params"
import {getPageFromHtabIndex} from "../helpers"


export const generateCombinedReportDialogSlice = {
  [AdasProgramsReportPages.SENSING_SYSTEM_REQUIREMENT]: {
    isDialogOpen: selectSensingSystemRequirementsGenerateCombinedReportDialogOpen,
    setIsDialogOpen: setSensingSystemRequirementsGenerateCombinedReportDialogOpen,
    dataSetStatusMap: selectSensingSystemRequirementsTableDataSetStatusMap,
    selectTableRows: selectSensingSystemRequirementsTableRows,
    updateReportEndpoint: CAdasProgramsRequests.generateCombinedReport.url,
    selectedReports: selectSensingSystemRequirementsTableSelectedRows,
  },
  [AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT]: {
    isDialogOpen: selectSensorComponentRequirementsGenerateCombinedReportDialogOpen,
    setIsDialogOpen: setSensorComponentRequirementsGenerateCombinedReportDialogOpen,
    dataSetStatusMap: selectSensorComponentRequirementsTableDataSetStatusMap,
    selectTableRows: selectSensorComponentRequirementsTableRows,
    updateReportEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorComponentRequirements.endpoints.generateCombinedReport,
    selectedReports: selectSensorComponentRequirementsTableSelectedRows,
  },
  [AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT]: {
    isDialogOpen: selectWorldModelSolutionRequirementsGenerateCombinedReportDialogOpen,
    setIsDialogOpen: setWorldModelSolutionRequirementsGenerateCombinedReportDialogOpen,
    dataSetStatusMap: selectWorldModelSolutionRequirementsTableDataSetStatusMap,
    selectTableRows: selectWorldModelSolutionRequirementsTableRows,
    updateReportEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.worldModelSolutionRequirements.endpoints.generateCombinedReport,
    selectedReports: selectWorldModelSolutionRequirementsTableSelectedRows,
  },
}

export const GenerateCombinedReportDialog = () => {
  const dispatch = useAppDispatch()

  const htabid = ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabIndexUrlKey
  const [htab, setHtab] = useQueryParam(htabid, StringParam)
  const page = useMemo(() => getPageFromHtabIndex(htab as AdasProgramsReportPageIdx), [htab])

  const selectedReports = useAppSelector(generateCombinedReportDialogSlice[page].selectedReports)

  const isOpen = useAppSelector(generateCombinedReportDialogSlice[page].isDialogOpen)
  const closeDialog = () => {
    dispatch(generateCombinedReportDialogSlice[page].setIsDialogOpen(false))
  }
  const dataSetStatusMap = useAppSelector(generateCombinedReportDialogSlice[page].dataSetStatusMap)
  const tableRows = useAppSelector(generateCombinedReportDialogSlice[page].selectTableRows)

  const adasProgramMetaData = useAppSelector(selectAdasProgramsMetaData)
  const adasProgramId = adasProgramMetaData?.adasProgramId

  const [hasUpdatedReports, setHasUpdatedReports] = useState(EUpdateReportsStatus.NOT_STARTED)
  const [isUpdateReportsLoading, setIsUpdateReportsLoading] = useState(false)

  const downloadHtmlReport = (htmlString: string, filename = "combined_report.html") => {
    const blob = new Blob([htmlString], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /** Request handles */
  const updateReportsHandle = usePostRequest(generateCombinedReportDialogSlice[page].updateReportEndpoint)

  const updateReports = () => {
    // Get reports data {id, datasetId, format). So this will update the reports that are loaded in this client instance.
    interface ReportData {
      datasetId: string;
      reportFormat: string;
      datasetState: EArgosReportsState;
    }

    const reportsData: Record<string, ReportData> = {}
    for (const row of tableRows) {
      const datasetState: EArgosReportsState = (dataSetStatusMap?.[row.reportId] ?? row.dataSetState) as EArgosReportsState

      // Only update selected reports
      if (!selectedReports[row.reportId]) {
        continue
      }

      const datasetId = page === AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT
        ? (row as GreenLineReportsTableData).greenLineDataSetId
        : (row as RedLineReportsTableData).redLineDataSetId
      reportsData[row.reportId] = {
        datasetId,
        reportFormat: row.reportFormat,
        datasetState
      }
    }

    setIsUpdateReportsLoading(true)
    setHasUpdatedReports(EUpdateReportsStatus.IN_PROGRESS)
    toast.loading(`Generating combined report for ${ADAS_PROGRAMS_REPORT_PAGE_NAME_MAP[page]}...`, {id: `${page}-generate-combined-report-toast`});
    updateReportsHandle({
      'action': 'generateCombinedReport',
      adasProgramId,
      page,
      reportsData,
      originUrl: window.location.origin
    }).then((res) => {
      // downloadHtmlReport(res, `combined_report_${page}.pdf`)
      downloadHtmlReport(res, `combined_report_${page}.html`)

      setHasUpdatedReports(EUpdateReportsStatus.SUCCESS)
    }).catch((err) => {
      console.log('An error occurred while updating reports', err)
      toast.error('An error occurred while updating reports')
      setHasUpdatedReports(EUpdateReportsStatus.FAILED)
    }).finally(() => {
      setIsUpdateReportsLoading(false)
      toast.dismiss(`${page}-generate-combined-report-toast`);
    })
  }


  return (
    <ArgosDialog
      width='auto'
      height='auto'
      open={isOpen}
      onClose={closeDialog}
      headerTitle="Download Combined Report"
    >
      <Loader isLoading={false}>
        <ArgosFlexbox vertical style={{height: '100%'}}>
          <ArgosFlexbox style={{justifyContent: 'center', marginTop: 'auto'}}>
            <ArgosButton onClick={() => closeDialog()} disabled={isUpdateReportsLoading}>
              Cancel
            </ArgosButton>
            <ArgosLoadingButton
              isLoading={isUpdateReportsLoading}
              onClick={() => {
                updateReports()
                closeDialog()
              }}
            >
              Download
            </ArgosLoadingButton>
          </ArgosFlexbox>
        </ArgosFlexbox>
      </Loader>
    </ArgosDialog>
  )
}
